import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, Search, Edit2, Trash2, Save, X, 
  Mail, Phone, Calendar, Award, Download, Upload,
  AlertCircle, Check, User, Wrench, TrendingUp, Eye,
  FileText, CheckCircle, FolderPlus, Settings, Folder
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

const ApprenantsManager = () => {
  const [apprenants, setApprenants] = useState([]);
  const [interventionsData, setInterventionsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroupe, setFilterGroupe] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showGroupesModal, setShowGroupesModal] = useState(false);
  const [selectedApprenant, setSelectedApprenant] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [importData, setImportData] = useState({ file: null, preview: [] });

  // État pour la gestion des groupes
  const [groupes, setGroupes] = useState([]);
  const [newGroupeName, setNewGroupeName] = useState('');
  const [editingGroupeId, setEditingGroupeId] = useState(null);
  const [editingGroupeName, setEditingGroupeName] = useState('');

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    groupe: ''
  });

  // Groupes par défaut (utilisés si aucun groupe n'existe)
  const defaultGroupes = [
    "CAP 1 A MVA", "CAP 1 B MVA", "CAP 2 A MVA", "CAP 2 B MVA",
    "CAP 1 AN PA", "CAP 2 CA", "CAP 1 CA",
    "BCP 1/3 MVA", "BCP 2/3 A MVA", "BCP 2/3 B MVA", "BCP 3/3 A MVA", "BCP 3/3 B MVA",
    "CQP_TAVA"
  ];

  useEffect(() => {
    loadData();
    loadGroupes();
    checkUserRole();
  }, []);

  const checkUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        setCurrentUserRole(profile?.role);
      }
    } catch (error) {
      console.error('Erreur vérification rôle:', error);
    }
  };

  const loadGroupes = async () => {
    try {
      // Essayer de charger depuis la table groupes
      const { data: groupesData, error: groupesError } = await supabase
        .from('groupes')
        .select('*')
        .order('nom');

      if (groupesError) {
        // Si la table n'existe pas, extraire les groupes uniques des apprenants
        console.log('Table groupes non trouvée, extraction depuis apprenants...');
        const { data: apprenantsData } = await supabase
          .from('apprenants')
          .select('groupe');
        
        if (apprenantsData) {
          const uniqueGroupes = [...new Set(apprenantsData.map(a => a.groupe).filter(Boolean))];
          // Combiner avec les groupes par défaut
          const allGroupes = [...new Set([...defaultGroupes, ...uniqueGroupes])];
          setGroupes(allGroupes.sort().map((nom, index) => ({ id: `temp-${index}`, nom })));
        } else {
          setGroupes(defaultGroupes.map((nom, index) => ({ id: `temp-${index}`, nom })));
        }
      } else {
        setGroupes(groupesData || []);
      }
    } catch (error) {
      console.error('Erreur chargement groupes:', error);
      setGroupes(defaultGroupes.map((nom, index) => ({ id: `temp-${index}`, nom })));
    }
  };

  const loadData = async () => {
    try {
      // Charger les apprenants
      const { data: apprenantsData, error: apprenantsError } = await supabase
        .from('apprenants')
        .select('*')
        .order('nom');

      if (apprenantsError) throw apprenantsError;

      // Charger les interventions avec les compétences
      const { data: interventionsData, error: interventionsError } = await supabase
        .from('interventions')
        .select('*');

      if (interventionsError) throw interventionsError;

      // Organiser les données par apprenant
      const interventionsByApprenant = {};
      
      interventionsData.forEach(intervention => {
        if (intervention.apprentices && Array.isArray(intervention.apprentices)) {
          intervention.apprentices.forEach(apprenticeId => {
            if (!interventionsByApprenant[apprenticeId]) {
              interventionsByApprenant[apprenticeId] = {
                count: 0,
                competences: new Set(),
                interventions: []
              };
            }
            
            interventionsByApprenant[apprenticeId].count++;
            interventionsByApprenant[apprenticeId].interventions.push(intervention);
            
            // Ajouter les compétences
            if (intervention.competences_travaillees && Array.isArray(intervention.competences_travaillees)) {
              intervention.competences_travaillees.forEach(comp => {
                if (comp.competenceCode) {
                  interventionsByApprenant[apprenticeId].competences.add(comp.competenceCode);
                }
              });
            }
          });
        }
      });

      // Convertir les Sets en Arrays pour l'affichage
      Object.keys(interventionsByApprenant).forEach(key => {
        interventionsByApprenant[key].competences = Array.from(interventionsByApprenant[key].competences);
      });

      setApprenants(apprenantsData || []);
      setInterventionsData(interventionsByApprenant);
    } catch (error) {
      console.error('Erreur chargement:', error);
      showMessage('error', 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      groupe: ''
    });
    setEditingId(null);
  };

  // ===== GESTION DES GROUPES =====

  const handleAddGroupe = async () => {
    if (!newGroupeName.trim()) {
      showMessage('error', 'Veuillez entrer un nom de groupe');
      return;
    }

    // Vérifier si le groupe existe déjà
    const groupeExists = groupes.some(g => g.nom.toLowerCase() === newGroupeName.trim().toLowerCase());
    if (groupeExists) {
      showMessage('error', 'Ce groupe existe déjà');
      return;
    }

    try {
      // Essayer d'insérer dans la table groupes
      const { data, error } = await supabase
        .from('groupes')
        .insert([{ nom: newGroupeName.trim().toUpperCase() }])
        .select()
        .single();

      if (error) {
        // Si la table n'existe pas, ajouter localement
        if (error.code === '42P01') {
          const newGroupe = { id: `temp-${Date.now()}`, nom: newGroupeName.trim().toUpperCase() };
          setGroupes(prev => [...prev, newGroupe].sort((a, b) => a.nom.localeCompare(b.nom)));
          showMessage('success', 'Groupe ajouté (mode local)');
        } else {
          throw error;
        }
      } else {
        setGroupes(prev => [...prev, data].sort((a, b) => a.nom.localeCompare(b.nom)));
        showMessage('success', 'Groupe créé avec succès');
      }

      setNewGroupeName('');
    } catch (error) {
      console.error('Erreur création groupe:', error);
      showMessage('error', 'Erreur lors de la création du groupe');
    }
  };

  const handleUpdateGroupe = async (groupeId, oldName) => {
    if (!editingGroupeName.trim()) {
      showMessage('error', 'Le nom du groupe ne peut pas être vide');
      return;
    }

    // Vérifier si le nouveau nom existe déjà
    const groupeExists = groupes.some(g => 
      g.id !== groupeId && g.nom.toLowerCase() === editingGroupeName.trim().toLowerCase()
    );
    if (groupeExists) {
      showMessage('error', 'Ce nom de groupe existe déjà');
      return;
    }

    try {
      const newName = editingGroupeName.trim().toUpperCase();

      // Mettre à jour dans la table groupes si elle existe
      if (!groupeId.toString().startsWith('temp-')) {
        const { error: groupeError } = await supabase
          .from('groupes')
          .update({ nom: newName })
          .eq('id', groupeId);

        if (groupeError && groupeError.code !== '42P01') {
          throw groupeError;
        }
      }

      // Mettre à jour tous les apprenants qui ont cet ancien groupe
      const { error: apprenantsError } = await supabase
        .from('apprenants')
        .update({ groupe: newName })
        .eq('groupe', oldName);

      if (apprenantsError) throw apprenantsError;

      // Mettre à jour l'état local
      setGroupes(prev => prev.map(g => 
        g.id === groupeId ? { ...g, nom: newName } : g
      ).sort((a, b) => a.nom.localeCompare(b.nom)));

      setEditingGroupeId(null);
      setEditingGroupeName('');
      showMessage('success', 'Groupe modifié avec succès');
      
      // Recharger les apprenants pour refléter les changements
      loadData();
    } catch (error) {
      console.error('Erreur modification groupe:', error);
      showMessage('error', 'Erreur lors de la modification du groupe');
    }
  };

  const handleDeleteGroupe = async (groupeId, groupeName) => {
    // Vérifier si des apprenants utilisent ce groupe
    const apprenantsInGroupe = apprenants.filter(a => a.groupe === groupeName);
    
    if (apprenantsInGroupe.length > 0) {
      if (!confirm(`Attention : ${apprenantsInGroupe.length} apprenant(s) sont dans ce groupe. Voulez-vous vraiment le supprimer ? Les apprenants garderont leur groupe actuel mais il ne sera plus disponible dans la liste.`)) {
        return;
      }
    } else {
      if (!confirm('Êtes-vous sûr de vouloir supprimer ce groupe ?')) {
        return;
      }
    }

    try {
      // Supprimer de la table groupes si elle existe
      if (!groupeId.toString().startsWith('temp-')) {
        const { error } = await supabase
          .from('groupes')
          .delete()
          .eq('id', groupeId);

        if (error && error.code !== '42P01') {
          throw error;
        }
      }

      // Mettre à jour l'état local
      setGroupes(prev => prev.filter(g => g.id !== groupeId));
      showMessage('success', 'Groupe supprimé avec succès');
    } catch (error) {
      console.error('Erreur suppression groupe:', error);
      showMessage('error', 'Erreur lors de la suppression du groupe');
    }
  };

  // ===== FIN GESTION DES GROUPES =====

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom || !formData.prenom || !formData.groupe) {
      showMessage('error', 'Veuillez remplir tous les champs');
      return;
    }

    try {
      if (editingId) {
        const { error } = await supabase
          .from('apprenants')
          .update(formData)
          .eq('id', editingId);

        if (error) throw error;
        showMessage('success', 'Apprenant mis à jour');
      } else {
        const { error } = await supabase
          .from('apprenants')
          .insert([formData]);

        if (error) throw error;
        showMessage('success', 'Apprenant ajouté');
      }

      loadData();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showMessage('error', 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (apprenant) => {
    setFormData({
      nom: apprenant.nom,
      prenom: apprenant.prenom,
      groupe: apprenant.groupe
    });
    setEditingId(apprenant.id);
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet apprenant ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('apprenants')
        .delete()
        .eq('id', id);

      if (error) throw error;

      loadData();
      showMessage('success', 'Apprenant supprimé');
    } catch (error) {
      console.error('Erreur suppression:', error);
      showMessage('error', 'Erreur lors de la suppression');
    }
  };

  const handleViewDetails = (apprenant) => {
    setSelectedApprenant(apprenant);
    setShowDetailsModal(true);
  };

  const exportApprenants = () => {
    const csv = [
      ['NOM_APPRENANT', 'PRENOM_APPRENANT', 'NOM_GROUPE_APPRENANT', 'INTERVENTIONS', 'COMPETENCES'].join(';'),
      ...apprenants.map(a => {
        const stats = interventionsData[a.id] || { count: 0, competences: [] };
        return [
          a.nom, 
          a.prenom, 
          a.groupe,
          stats.count,
          stats.competences.join(',')
        ].join(';');
      })
    ].join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `apprenants_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showMessage('success', 'Export réussi');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      // Supprimer la ligne d'en-tête
      const dataLines = lines.slice(1);
      
      const parsedData = dataLines.map(line => {
        const [nom, prenom, groupe] = line.split(';').map(field => field.trim());
        return { nom, prenom, groupe };
      }).filter(item => item.nom && item.prenom && item.groupe);

      setImportData({
        file: file.name,
        preview: parsedData
      });
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImportCSV = async () => {
    if (importData.preview.length === 0) {
      showMessage('error', 'Aucune donnée à importer');
      return;
    }

    try {
      setLoading(true);

      // Insérer les nouveaux apprenants
      const { error: insertError } = await supabase
        .from('apprenants')
        .insert(importData.preview);

      if (insertError) throw insertError;

      // Ajouter les nouveaux groupes à la liste
      const newGroupesFromImport = [...new Set(importData.preview.map(a => a.groupe))];
      const existingGroupeNames = groupes.map(g => g.nom);
      const groupesToAdd = newGroupesFromImport.filter(g => !existingGroupeNames.includes(g));
      
      if (groupesToAdd.length > 0) {
        // Essayer d'ajouter à la table groupes
        try {
          await supabase.from('groupes').insert(groupesToAdd.map(nom => ({ nom })));
        } catch (e) {
          // Ignorer si la table n'existe pas
        }
        loadGroupes();
      }

      showMessage('success', `${importData.preview.length} apprenants importés avec succès`);
      setShowImportModal(false);
      setImportData({ file: null, preview: [] });
      loadData();
    } catch (error) {
      console.error('Erreur import:', error);
      showMessage('error', 'Erreur lors de l\'import : ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredApprenants = apprenants.filter(app => {
    const matchSearch = !searchTerm || 
      app.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.groupe.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchGroupe = filterGroupe === 'all' || app.groupe === filterGroupe;

    return matchSearch && matchGroupe;
  });

  const stats = {
    total: apprenants.length,
    groupes: [...new Set(apprenants.map(a => a.groupe))].length,
    totalInterventions: Object.values(interventionsData).reduce((sum, data) => sum + data.count, 0)
  };

  // Obtenir le nombre d'apprenants par groupe
  const getApprenantsCountByGroupe = (groupeName) => {
    return apprenants.filter(a => a.groupe === groupeName).length;
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-6">
        {/* Messages */}
        <AnimatePresence>
          {message.text && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`p-4 rounded-lg mb-6 flex items-center gap-3 ${
                message.type === 'success' 
                  ? 'bg-green-600/20 border border-green-600' 
                  : 'bg-red-600/20 border border-red-600'
              }`}
            >
              {message.type === 'success' ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-400" />
              )}
              <span className="text-white">{message.text}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-orange-600 p-3 rounded-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Gestion des Apprenants</h1>
                <p className="text-gray-300">Suivi des élèves et de leurs compétences</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowGroupesModal(true)}
                className="bg-purple-600 hover:bg-purple-700 px-4 py-3 rounded-lg flex items-center gap-2 transition-colors text-white"
                title="Gérer les groupes"
              >
                <Settings className="w-5 h-5" />
                <span className="hidden md:inline">Groupes</span>
              </button>
              <button
                onClick={exportApprenants}
                className="bg-green-600 hover:bg-green-700 px-4 py-3 rounded-lg flex items-center gap-2 transition-colors"
                title="Exporter CSV"
              >
                <Download className="w-5 h-5" />
              </button>
              {currentUserRole === 'super_admin' && (
                <button
                  onClick={() => setShowImportModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-3 rounded-lg flex items-center gap-2 transition-colors"
                  title="Importer CSV"
                >
                  <Upload className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg flex items-center gap-2 transition-colors text-white"
              >
                <Plus className="w-5 h-5" />
                Nouvel Apprenant
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Total apprenants</p>
              <p className="text-3xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Groupes</p>
              <p className="text-3xl font-bold text-blue-400">{stats.groupes}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Interventions</p>
              <p className="text-3xl font-bold text-purple-400">{stats.totalInterventions}</p>
            </div>
          </div>

          {/* Filtres */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher un apprenant..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
            <div className="w-full md:w-64">
              <select
                value={filterGroupe}
                onChange={(e) => setFilterGroupe(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">Tous les groupes</option>
                {groupes.map(groupe => (
                  <option key={groupe.id} value={groupe.nom}>{groupe.nom}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Liste */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
            <p className="mt-4 text-gray-400">Chargement...</p>
          </div>
        ) : filteredApprenants.length === 0 ? (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-12 text-center">
            <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Aucun apprenant trouvé
            </h3>
            <p className="text-gray-400">
              {searchTerm ? 'Essayez avec d\'autres termes' : 'Commencez par ajouter un apprenant ou importer un CSV'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredApprenants.map((apprenant, index) => {
              const apprenantStats = interventionsData[apprenant.id] || { count: 0, competences: [], interventions: [] };
              
              return (
                <motion.div
                  key={apprenant.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6 hover:border-orange-500 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="bg-orange-600/20 p-3 rounded-lg">
                        <User className="w-6 h-6 text-orange-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-white">
                            {apprenant.prenom} {apprenant.nom}
                          </h3>
                        </div>
                        
                        <div className="space-y-1 text-sm text-gray-400 mb-3">
                          <div className="flex items-center gap-2">
                            <Award className="w-4 h-4" />
                            <span className="font-medium text-orange-400">{apprenant.groupe}</span>
                          </div>
                        </div>

                        {/* Stats et compétences */}
                        <div className="pt-3 border-t border-slate-700">
                          <div className="flex items-center gap-4 mb-3 text-sm">
                            <div className="flex items-center gap-2 text-blue-400">
                              <Wrench className="w-4 h-4" />
                              <span className="font-semibold">{apprenantStats.count}</span>
                              <span className="text-gray-400">intervention{apprenantStats.count > 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex items-center gap-2 text-purple-400">
                              <TrendingUp className="w-4 h-4" />
                              <span className="font-semibold">{apprenantStats.competences.length}</span>
                              <span className="text-gray-400">compétence{apprenantStats.competences.length > 1 ? 's' : ''}</span>
                            </div>
                          </div>

                          {/* Aperçu des compétences */}
                          {apprenantStats.competences.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-400 mb-2">Compétences travaillées :</p>
                              <div className="flex flex-wrap gap-2">
                                {apprenantStats.competences.slice(0, 5).map((code, idx) => (
                                  <span key={idx} className="bg-orange-600/20 text-orange-400 px-2 py-1 rounded text-xs font-medium">
                                    {code}
                                  </span>
                                ))}
                                {apprenantStats.competences.length > 5 && (
                                  <span className="text-gray-500 text-xs flex items-center">
                                    +{apprenantStats.competences.length - 5} autres
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {apprenantStats.count === 0 && (
                            <p className="text-sm text-gray-500 italic">
                              Aucune intervention enregistrée pour cet apprenant
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {apprenantStats.count > 0 && (
                        <button
                          onClick={() => handleViewDetails(apprenant)}
                          className="p-2 text-purple-400 hover:text-purple-300 hover:bg-slate-700 rounded-lg transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(apprenant)}
                        className="p-2 text-blue-400 hover:text-blue-300 hover:bg-slate-700 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(apprenant.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-slate-700 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Modal de gestion des groupes */}
        <AnimatePresence>
          {showGroupesModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => {
                setShowGroupesModal(false);
                setEditingGroupeId(null);
                setEditingGroupeName('');
                setNewGroupeName('');
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-800 rounded-xl shadow-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-600 p-2 rounded-lg">
                      <Folder className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Gestion des Groupes</h2>
                      <p className="text-gray-400 text-sm">{groupes.length} groupe(s) disponible(s)</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowGroupesModal(false);
                      setEditingGroupeId(null);
                      setEditingGroupeName('');
                      setNewGroupeName('');
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Formulaire d'ajout de groupe */}
                <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <FolderPlus className="w-5 h-5 text-green-400" />
                    Créer un nouveau groupe
                  </h3>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={newGroupeName}
                      onChange={(e) => setNewGroupeName(e.target.value)}
                      placeholder="Nom du groupe (ex: BTS 1 MV)"
                      className="flex-1 bg-slate-600 border border-slate-500 rounded-lg py-3 px-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddGroupe()}
                    />
                    <button
                      onClick={handleAddGroupe}
                      className="bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg flex items-center gap-2 transition-colors text-white"
                    >
                      <Plus className="w-5 h-5" />
                      Ajouter
                    </button>
                  </div>
                </div>

                {/* Liste des groupes */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Liste des groupes</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {groupes.length === 0 ? (
                      <p className="text-gray-400 text-center py-8">Aucun groupe créé</p>
                    ) : (
                      groupes.map((groupe) => {
                        const count = getApprenantsCountByGroupe(groupe.nom);
                        const isEditing = editingGroupeId === groupe.id;

                        return (
                          <div
                            key={groupe.id}
                            className="bg-slate-700/50 rounded-lg p-4 flex items-center justify-between gap-4"
                          >
                            {isEditing ? (
                              <div className="flex-1 flex gap-3">
                                <input
                                  type="text"
                                  value={editingGroupeName}
                                  onChange={(e) => setEditingGroupeName(e.target.value)}
                                  className="flex-1 bg-slate-600 border border-purple-500 rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  autoFocus
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleUpdateGroupe(groupe.id, groupe.nom);
                                    }
                                  }}
                                />
                                <button
                                  onClick={() => handleUpdateGroupe(groupe.id, groupe.nom)}
                                  className="bg-green-600 hover:bg-green-700 p-2 rounded-lg transition-colors"
                                  title="Sauvegarder"
                                >
                                  <Check className="w-5 h-5 text-white" />
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingGroupeId(null);
                                    setEditingGroupeName('');
                                  }}
                                  className="bg-slate-600 hover:bg-slate-500 p-2 rounded-lg transition-colors"
                                  title="Annuler"
                                >
                                  <X className="w-5 h-5 text-white" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-3 flex-1">
                                  <div className="bg-purple-600/20 p-2 rounded-lg">
                                    <Folder className="w-5 h-5 text-purple-400" />
                                  </div>
                                  <div>
                                    <p className="text-white font-medium">{groupe.nom}</p>
                                    <p className="text-sm text-gray-400">
                                      {count} apprenant{count > 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      setEditingGroupeId(groupe.id);
                                      setEditingGroupeName(groupe.nom);
                                    }}
                                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-slate-600 rounded-lg transition-colors"
                                    title="Modifier"
                                  >
                                    <Edit2 className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteGroupe(groupe.id, groupe.nom)}
                                    className="p-2 text-red-400 hover:text-red-300 hover:bg-slate-600 rounded-lg transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Note informative */}
                <div className="mt-6 bg-blue-600/20 border border-blue-600/50 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div className="text-sm text-blue-200">
                      <p className="font-semibold mb-1">Information</p>
                      <p>Modifier le nom d'un groupe mettra automatiquement à jour tous les apprenants associés à ce groupe.</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal d'ajout/édition */}
        <AnimatePresence>
          {showAddModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-800 rounded-xl shadow-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">
                    {editingId ? 'Modifier l\'apprenant' : 'Nouvel apprenant'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Nom *
                      </label>
                      <input
                        type="text"
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value.toUpperCase() })}
                        required
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 uppercase"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        value={formData.prenom}
                        onChange={(e) => setFormData({ ...formData, prenom: e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1).toLowerCase() })}
                        required
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Groupe *
                      </label>
                      <select
                        value={formData.groupe}
                        onChange={(e) => setFormData({ ...formData, groupe: e.target.value })}
                        required
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Sélectionner un groupe...</option>
                        {groupes.sort((a, b) => a.nom.localeCompare(b.nom)).map(groupe => (
                          <option key={groupe.id} value={groupe.nom}>{groupe.nom}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        resetForm();
                      }}
                      className="flex-1 bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-lg transition-colors text-white"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 text-white"
                    >
                      <Save className="w-5 h-5" />
                      {editingId ? 'Modifier' : 'Créer'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal d'import CSV */}
        <AnimatePresence>
          {showImportModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => {
                setShowImportModal(false);
                setImportData({ file: null, preview: [] });
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-800 rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white">Importer des apprenants</h2>
                    <p className="text-gray-400 text-sm mt-1">Format CSV attendu : NOM_APPRENANT;PRENOM_APPRENANT;NOM_GROUPE_APPRENANT</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowImportModal(false);
                      setImportData({ file: null, preview: [] });
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Zone d'upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Fichier CSV
                    </label>
                    <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-orange-500 transition-colors">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="csv-upload"
                      />
                      <label
                        htmlFor="csv-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="w-12 h-12 text-gray-400 mb-3" />
                        <p className="text-white font-medium mb-1">
                          {importData.file || 'Cliquez pour sélectionner un fichier CSV'}
                        </p>
                        <p className="text-sm text-gray-400">
                          Ou glissez-déposez votre fichier ici
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Aperçu des données */}
                  {importData.preview.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                          <FileText className="w-5 h-5 text-blue-400" />
                          Aperçu ({importData.preview.length} apprenants)
                        </h3>
                        <span className="bg-green-600/20 text-green-400 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Prêt à importer
                        </span>
                      </div>
                      
                      <div className="bg-slate-900/50 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <div className="space-y-2">
                          {importData.preview.slice(0, 10).map((item, idx) => (
                            <div key={idx} className="bg-slate-800 rounded p-3 flex items-center gap-3">
                              <div className="bg-orange-600/20 p-2 rounded">
                                <User className="w-4 h-4 text-orange-400" />
                              </div>
                              <div className="flex-1">
                                <p className="text-white font-medium">
                                  {item.prenom} {item.nom}
                                </p>
                                <p className="text-sm text-gray-400">{item.groupe}</p>
                              </div>
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            </div>
                          ))}
                          {importData.preview.length > 10 && (
                            <p className="text-center text-gray-400 text-sm py-2">
                              ... et {importData.preview.length - 10} autres apprenants
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowImportModal(false);
                        setImportData({ file: null, preview: [] });
                      }}
                      className="flex-1 bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-lg transition-colors text-white"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleImportCSV}
                      disabled={importData.preview.length === 0 || loading}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 text-white"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                          Import en cours...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          Importer {importData.preview.length} apprenants
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal de détails des compétences */}
        <AnimatePresence>
          {showDetailsModal && selectedApprenant && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => {
                setShowDetailsModal(false);
                setSelectedApprenant(null);
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-800 rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      {selectedApprenant.prenom} {selectedApprenant.nom}
                    </h2>
                    <p className="text-gray-400">{selectedApprenant.groupe}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedApprenant(null);
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {(() => {
                  const stats = interventionsData[selectedApprenant.id] || { count: 0, competences: [], interventions: [] };
                  
                  return (
                    <div className="space-y-6">
                      {/* Statistiques */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-700/50 rounded-lg p-4">
                          <p className="text-gray-400 text-sm mb-1">Total interventions</p>
                          <p className="text-2xl font-bold text-blue-400">{stats.count}</p>
                        </div>
                        <div className="bg-slate-700/50 rounded-lg p-4">
                          <p className="text-gray-400 text-sm mb-1">Compétences travaillées</p>
                          <p className="text-2xl font-bold text-purple-400">{stats.competences.length}</p>
                        </div>
                      </div>

                      {/* Liste des compétences */}
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                          <Award className="w-5 h-5 text-orange-400" />
                          Toutes les compétences
                        </h3>
                        {stats.competences.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {stats.competences.map((code, idx) => (
                              <span key={idx} className="bg-orange-600/20 text-orange-400 px-3 py-2 rounded-lg text-sm font-medium">
                                {code}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 italic">Aucune compétence enregistrée</p>
                        )}
                      </div>

                      {/* Liste des interventions */}
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                          <Wrench className="w-5 h-5 text-blue-400" />
                          Historique des interventions
                        </h3>
                        <div className="space-y-3">
                          {stats.interventions.map((intervention) => (
                            <div key={intervention.id} className="bg-slate-700/50 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">
                                      {intervention.immatriculation}
                                    </span>
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                      intervention.intervention_type === 'mechanical' 
                                        ? 'bg-green-600 text-white' 
                                        : 'bg-purple-600 text-white'
                                    }`}>
                                      {intervention.intervention_type === 'mechanical' ? 'Mécanique' : 'Carrosserie'}
                                    </span>
                                  </div>
                                  <p className="text-white font-medium">{intervention.client_name}</p>
                                  <p className="text-sm text-gray-400 line-clamp-1">{intervention.work_description}</p>
                                </div>
                                <div className="text-right text-sm text-gray-400">
                                  {new Date(intervention.completion_date).toLocaleDateString('fr-FR')}
                                </div>
                              </div>
                              
                              {intervention.competences_travaillees && intervention.competences_travaillees.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-slate-600">
                                  <div className="flex flex-wrap gap-1">
                                    {[...new Set(intervention.competences_travaillees.map(c => c.competenceCode))].map((code, idx) => (
                                      <span key={idx} className="bg-orange-600/20 text-orange-400 px-2 py-1 rounded text-xs">
                                        {code}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ApprenantsManager;